/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, useEffect, useRef, ReactElement, useMemo, Fragment } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { isDarkMode } from '../../utils/helpers';
import {
  sendOffer,
  sendCancel,
  sendSuggest,
  sendAmountCustom,
  counterOffer,
  counterCancel,
  counterModalCustomAmount,
} from '../../actions/profileTrackAction';
import OffersTutorialModal from '../../components/common/modals/OffersTutorialModal';
import InitialOfferPanel from './InitialOfferPanel';
import { getCompletionTheme, getTextColor, type } from '../../style';
import PendingOfferPanel from './PendingOfferPanel';
import AcceptedOfferPanel from './AcceptedOfferPanel';
import useOnClickTriggerTracking from '../../hooks/useOnclickTriggerTracking';
import { getDefaultOfferAmount } from '../../utils/offers';
import { useProfile } from '../../contexts/ProfileContext';
import { useApp } from '../../contexts/AppContext';
import useMixPanel from '../../hooks/useMixPanel';
import { EVENT_NAMES } from '../../constants/mixpanel';
import { AccountMaker } from '../../models/Account';
import { canGenerousUserMakeOffer, defaultOfferAmount, renderAvatar } from '../../common';
import { IOfferPanelV2Offer } from '../../types/common';
import { IOfferPanelProps } from './types/offerPanel';
import { useDispatch, useSelector } from 'react-redux';
import { clearErrors } from '../../actions/commonActions';
import { updateShowTutorialOffer } from '../../actions/profileActions';
import breakpoints from '../../theme/material/breakpoints';
import useMandatoryWall from '../../hooks/useMandatoryWall';
import { useCommonModal } from 'contexts/ModalContext';
import {
  acceptOffer,
  createNewOffer,
  sendSuggestion,
  setShowOfferSuccess,
  showCreateNewOfferModal,
} from 'actions/offerActions';
import { RootState } from 'store';
import OfferSuccess from 'components/pages/Offers/OfferSuccess';
import CreateNewOffer from 'components/pages/Offers/CreateNewOffer';

const Form = styled('div')<{ bgColor: string }>(
  {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    borderRadius: 6,
    padding: '20px 60px',
    height: 'auto',
    gap: 12,
    backgroundColor: getCompletionTheme().containerBG,
    [breakpoints.down(620.05)]: {
      minHeight: '100%',
      maxHeight: '100%',
      height: '100%',
      padding: '15px 20px',
    },
  },
  ({ bgColor }) => ({
    background: bgColor,
  })
);

const HeaderDiv = styled('div')<{ moreSpacing: boolean }>(
  {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: 10,
  },
  ({ moreSpacing }) => ({
    marginTop: 0,
    marginBottom: moreSpacing ? 0 : 'auto',

    [breakpoints.down(639.05)]: {
      marginTop: 0,
    },
    [breakpoints.down(375.05)]: {
      marginTop: 0,
      maxWidth: 310,
      margin: 'auto',
    },
  })
);

const MainText = styled('span')(() => ({
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: -0.3,
  textAlign: 'center',
  ...getTextColor().LIGHT_GRAY,
}));

const SubText = styled('span')(() => ({
  textAlign: 'center',
  ...type.XS,
  lineHeight: '16px',
  letterSpacing: -0.3,
  ...getTextColor().LIGHT_GRAY,
}));

const Content = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  justifyContent: 'space-between',
  gap: 15,
});

const offerMinMaxVal = (offer: IOfferPanelV2Offer, type: string, defaultAmount: string) => {
  if (!offer) return defaultAmount;

  if (type === 'min' && offer.suggest) {
    return defaultAmount;
  }

  return type === 'min' ? offer.min_counter_price : offer.max_counter_price;
};

const validateTextInput = (e: { key: any; preventDefault: () => void }) => {
  const Key = e.key;
  const regex = /[0-9]/;

  if (!regex.test(Key)) {
    if (e.preventDefault) e.preventDefault();
  }
};

const setCreateOfferAction = (_e: Event, offerAction: string) => {
  switch (offerAction) {
    case 'Send Counter':
      counterOffer();
      break;
    case 'Cancel Counter':
      counterCancel();
      break;
    case 'Send Offer':
      sendOffer();
      break;
    case 'Cancel Offer':
      sendCancel();
      break;
    case 'Suggest Offer':
      sendSuggest();
      break;
    default:
      return;
  }
};

const trackFocus = (ref, actionMethod) => {
  if (ref && ref.current) {
    ref.current.addEventListener('click', e => actionMethod(e), false);
  }

  return () => {
    ref.current.removeEventListener('click', () => null, false);
  };
};

const OfferPanelV2 = ({
  account,
  offerType,
  createdAt,
  myProfile,
  offer,
  isCounter,
  closeModal,
  currencyLabel,
  currencyInitial,
  currentUserIsGenerous,
  username,
  counterOffer,
  hashId,
  showOfferTutorial,
  isFirstTimeTutorial,
  unlockMessage,
  canMakeOffer,
  setJustSuggested,
}: IOfferPanelProps): ReactElement => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const otherProfile = account.profile.data;
  const accountModel = AccountMaker.create(myProfile);
  const { setShowTooManyPendingOfferModal, setModalArgs } = useCommonModal();

  const defaultOfferPrice = useMemo(() => {
    const defaultAmount = getDefaultOfferAmount();

    if (otherProfile.account_type === 'Attractive') {
      return otherProfile?.default_offer_amount ?? defaultAmount;
    }

    return defaultAmount;
  }, [otherProfile?.default_offer_amount]);

  const [offerPrice, setOfferPrice] = useState(isCounter ? '' : defaultOfferPrice);
  const [minValue, setMinValue] = useState(offerMinMaxVal(offer, 'min', defaultOfferAmount.minValue));
  const [maxValue, setMaxValue] = useState(offerMinMaxVal(offer, 'max', defaultOfferAmount.maxValue));
  const [hasErrors, setHasErrors] = useState(false);
  const [isOfferSending, setOfferSending] = useState(false);
  const [showCreateOfferModal, setShowCreateOfferModal] = useState(false);

  const acceptOfferBtn = useRef(null);
  const sendOfferBtn = useRef(null);
  const sendSuggestLink = useRef(null);
  const priceInput = useRef(null);
  const [isFirstTimeOfferTutorial, setFirstTimeOfferTutorial] = useState(false);
  const otherAccount = account;
  const isGenerous = currentUserIsGenerous;
  const iAmSender = offer ? offer.i_sent : false;
  const iAmAcceptor = offer ? offer.acceptor_hash_id === myProfile.hash_id : false;

  const { footerTopPosition } = useApp();
  const { calculateGalleryMediaRatio, offerBoxContainerRef, profileCardBannerRef } = useProfile();
  const { mixpanelData, resetMixpanelData } = useMixPanel();
  const { showMandatoryWall } = useMandatoryWall();
  const { showOfferSuccess, showCreateNewOfferModal: showCreateNewOffer } = useSelector(
    (state: RootState) => state.offers
  );

  useEffect(() => {
    setFirstTimeOfferTutorial(isFirstTimeTutorial);
  }, [isFirstTimeTutorial]);

  useEffect(() => {
    if (!isCounter && defaultOfferPrice && ['initial', 'suggestion_new'].includes(offerType)) {
      setOfferPrice(defaultOfferPrice);
    }
  }, [isCounter, defaultOfferPrice, offerType]);

  useEffect(() => {
    if (offer && offer.suggest) {
      setOfferPrice(offer.price_original ? offer.price_original : defaultOfferPrice);
    }
  }, [offer]);

  useEffect(() => {
    if (showCreateNewOffer) {
      const isAmSender = offer?.hash_id === myProfile?.hash_id;

      if (!isAmSender && myProfile.can_make_offer) {
        return;
      }

      showMandatoryWall(
        () => {
          setShowCreateOfferModal(true);
        },
        () => undefined,
        'Edit Sent Offer'
      );
    }
  }, [showCreateNewOffer]);

  useOnClickTriggerTracking(isCounter ? 'Send Counter' : 'Send Offer', sendOfferBtn, setCreateOfferAction);
  useOnClickTriggerTracking('Suggest Offer', sendSuggestLink, setCreateOfferAction);

  const handleClearErrors = () => {
    dispatch(clearErrors());
  };

  useEffect(() => {
    handleClearErrors();
    trackFocus(priceInput, isCounter ? counterModalCustomAmount : sendAmountCustom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setMaxValue(offerMinMaxVal(offer, 'max', defaultOfferAmount.maxValue));
    setMinValue(offerMinMaxVal(offer, 'min', defaultOfferAmount.minValue));
  }, [offer]);

  const updateOfferPrice = (val: React.SetStateAction<string | number | null>, error: boolean) => {
    //ignore this comment, prevented for re-rendering
    if (!error) {
      // handleClearErrors();
    }

    setOfferPrice(val);
    setHasErrors(error || false);
  };

  const inputOnChange = e => {
    const inputVal = Number(e.target.value);
    const minValueNum = Number(minValue);
    const maxValueNum = Number(maxValue);
    const maxLength = maxValue.toString().length;

    if (e.target.value.length <= maxLength) {
      const isError = inputVal < minValueNum || inputVal > maxValueNum;

      updateOfferPrice(inputVal, isError);
    } else {
      e.preventDefault();
    }
  };

  const currLabel = String.fromCharCode(currencyLabel as unknown as number);

  const mandatoryCompletionAction = (): void => {
    if (canGenerousUserMakeOffer(myProfile)) {
      if (accountModel.shouldShowTooManyLockedOffersDeals()) {
        setOfferSending(false);

        setShowTooManyPendingOfferModal(true);
        setModalArgs({
          onCloseCallback: () => {
            setOfferSending(false);
          },
        });

        return;
      }

      if (accountModel.shouldShowTooManySentOffers()) {
        setShowTooManyPendingOfferModal(true);
        setModalArgs({
          type: 'sent',
          onCloseCallback: () => {
            setOfferSending(false);
          },
        });
        return;
      }
    }

    // Send offer from Profile
    const mxOfferMadePayload = mixpanelData[EVENT_NAMES.OFFER_MADE];
    const formData = {
      price: offerPrice,
      account: hashId,
      source: mxOfferMadePayload.source,
    };
    const offerAmount = `${currencyInitial}${currLabel}${offerPrice}`;
    const amount = offerPrice === '???' ? '' : offerAmount;

    const canProceed = canMakeOffer();

    if (!canProceed && isGenerous) {
      setOfferSending(false);
      return;
    }

    if (offerPrice === '???') {
      setJustSuggested(true);
      dispatch(sendSuggestion(account.hash_id, false, null, null))
        .then(() => {
          closeModal();
          setOfferSending(false);
          handleOfferSuccess(true, amount, false, false, true);
        })
        .catch(() => {
          setOfferSending(false);
          setHasErrors(true);
        });
      return;
    }

    if (isCounter || (offer && offer.suggest)) {
      counterOffer(formData)
        .then(() => {
          closeModal();
          setOfferSending(false);
          handleOfferSuccess(true, amount);
          resetMixpanelData(EVENT_NAMES.OFFER_MADE);
        })
        .catch(() => {
          setOfferSending(false);
          setHasErrors(true);
        });
    } else {
      dispatch(createNewOffer(formData, false, null, null, false, amount))
        .then(() => {
          setOfferSending(false);
          resetMixpanelData(EVENT_NAMES.OFFER_MADE);
          closeModal();
        })
        .catch(() => {
          setOfferSending(false);
          setHasErrors(true);
        });
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    setOfferSending(true);

    showMandatoryWall(
      () => mandatoryCompletionAction(),
      () => setOfferSending(false),
      isCounter || (offer && offer.suggest) ? 'Counter Offer' : 'Make Offer'
    );
  };

  const getOfferText = (offerType: string) => {
    const offerOrRequest = isGenerous ? 'offer' : 'request';

    switch (offerType) {
      case 'initial': {
        return isGenerous ? (
          <MainText>
            Send <strong>{username}</strong> a first date incentive!
          </MainText>
        ) : (
          <MainText>
            Request a first date incentive from <strong>{username}</strong>.
          </MainText>
        );
      }
      case 'suggestion_new':
      case 'suggestion_pending': {
        return iAmSender ? (
          <MainText>
            <strong>{username}</strong> is considering your {offerOrRequest}.
          </MainText>
        ) : (
          <MainText>
            <strong>{username}</strong> requests you to suggest an offer amount.
          </MainText>
        );
      }
      case 'offer_new':
      case 'offer_pending': {
        return iAmSender ? (
          <MainText>
            <strong>{username}</strong> is considering your {offerOrRequest}.
          </MainText>
        ) : (
          <MainText>
            <strong>{username}</strong> sent you a date offer{isGenerous ? ' request' : ''}.
          </MainText>
        );
      }
      case 'offer_accepted':
        return iAmAcceptor ? (
          <MainText>
            You have accepted <strong>{username}</strong> {isGenerous ? 'request' : 'offer'}.
          </MainText>
        ) : (
          <MainText>
            <strong>{username}</strong> accepted your {isGenerous ? 'request' : 'offer'}.
          </MainText>
        );
      default:
        return '';
    }
  };

  const getOfferSubText = (offerType: string) => {
    switch (offerType) {
      case 'offer_new':
      case 'offer_pending':
      case 'suggestion_pending':
        return (
          iAmSender && (
            <SubText>
              <small style={{ fontSize: 12 }}>Click Offer Amount to edit your offer.</small>
            </SubText>
          )
        );
      case 'offer_accepted':
        return offer.is_deal_unlocked ? (
          <SubText>Messaging unlocked!</SubText>
        ) : isGenerous ? (
          <SubText>
            Unlock messaging for <strong>{offer.credit_cost} Credits</strong> and start planning your date!
          </SubText>
        ) : (
          <div />
        );
      default:
        return '';
    }
  };

  const getBgColor = (offerType: string) => {
    const darkMode = isDarkMode();

    if (offerType === 'initial' || offerType === 'offer_accepted') {
      return darkMode ? '#323232' : 'linear-gradient(180deg, #E4F8F6 0%, #C9F3EF 100%)';
    }

    return darkMode ? '#323232' : '#FFF3D9';
  };

  useEffect(() => {
    if (offerType) {
      calculateGalleryMediaRatio();
    }
  }, [offerType, footerTopPosition]);

  useEffect(() => {
    const onAnimationEnd = () => {
      calculateGalleryMediaRatio();
    };

    if (profileCardBannerRef.current) {
      profileCardBannerRef.current.addEventListener('animationend', onAnimationEnd);
    }

    return () => {
      if (profileCardBannerRef.current) {
        profileCardBannerRef.current.removeEventListener('animationend', onAnimationEnd);
      }
    };
  }, [profileCardBannerRef?.current, footerTopPosition]);

  const updateOfferTutorialState = () => {
    dispatch(updateShowTutorialOffer(false));
  };

  const handleAcceptOffer = async (hashId: string, message: string) => {
    dispatch(acceptOffer(hashId, message, false, null, null, false, 'Profile', navigate));
  };

  const handleOfferSuccess = (
    toggle: boolean,
    amount = '',
    isCounter = false,
    isRevised = false,
    isSuggest = false
  ) => {
    const offerAmount = `${currencyInitial}${currLabel}${offerPrice}`;

    dispatch(
      setShowOfferSuccess({
        isOpen: toggle,
        amount: amount ? amount : offerPrice === '???' ? '' : offerAmount,
        isCounter,
        isRevised,
        isSuggest,
      })
    );
  };

  const handleSendSuggestion = (hashId: string) => {
    return dispatch(sendSuggestion(hashId, false, null, null, false));
  };

  const handleCreateNewOffer = (formData: any, price: string | number) => {
    const offerAmount = `${currencyInitial}${currLabel}${price}`;
    const amount = price === '???' ? '' : offerAmount;

    return dispatch(createNewOffer(formData, false, null, null, false, amount));
  };

  return (
    <Fragment>
      <Form bgColor={getBgColor(offerType)} ref={offerBoxContainerRef}>
        <HeaderDiv moreSpacing={offerType === 'initial'}>
          <div>
            {getOfferText(offerType)}
            &ensp;
            {!isFirstTimeOfferTutorial && (
              <OffersTutorialModal
                showTutorial={false}
                onClose={updateOfferTutorialState}
                svgIconHeight={14}
                svgIconWeight={14}
                iconOnly
              />
            )}
          </div>
          <Fragment>{getOfferSubText(offerType)}</Fragment>
        </HeaderDiv>

        <Content>
          {['initial', 'suggestion_new'].includes(offerType) && (
            <InitialOfferPanel
              hasErrors={hasErrors}
              inputRef={priceInput}
              offerPrice={offerPrice}
              inputOnChange={inputOnChange}
              validateTextInput={validateTextInput}
              updateOfferPrice={updateOfferPrice}
              currencyInitial={currencyInitial}
              currLabel={currLabel}
              currentUserIsGenerous={isGenerous}
              offer={offer}
              createdAt={createdAt}
              handleSubmit={handleSubmit}
              sendOfferBtn={sendOfferBtn}
              isOfferSending={isOfferSending}
              otherAccount={otherAccount}
              isGenerous={isGenerous}
            />
          )}
          {['suggestion_pending', 'offer_new', 'offer_pending'].includes(offerType) && (
            <PendingOfferPanel
              offer={offer}
              createdAt={createdAt}
              myProfile={myProfile}
              otherProfile={otherProfile}
              otherAccount={otherAccount}
              isGenerous={isGenerous}
              acceptOffer={handleAcceptOffer}
              acceptOfferBtn={acceptOfferBtn}
              counterOffer={counterOffer}
            />
          )}
          {offerType === 'offer_accepted' && (
            <AcceptedOfferPanel
              offer={offer}
              createdAt={createdAt}
              offerPrice={offerPrice}
              myProfile={myProfile}
              otherProfile={otherProfile}
              otherAccount={otherAccount}
              isGenerous={isGenerous}
              unlockMessage={unlockMessage}
            />
          )}
          {isFirstTimeOfferTutorial && (
            <OffersTutorialModal
              showTutorial={showOfferTutorial}
              onClose={updateOfferTutorialState}
              isFirstTimeOffer={isFirstTimeOfferTutorial}
            />
          )}
          <OfferSuccess
            isModalOpen={showOfferSuccess.isOpen}
            offerAmount={showOfferSuccess.amount}
            avatarSrc={renderAvatar(otherProfile.avatar.data.urls, true)}
            username={otherAccount.username}
            onClose={() => {
              dispatch(
                setShowOfferSuccess({
                  isOpen: false,
                  amount: '',
                  isCounter: false,
                  isRevised: false,
                  isSuggest: false,
                })
              );
            }}
            buttonID="interestedOffers"
            isCounter={showOfferSuccess?.isCounter}
            isRevised={showOfferSuccess?.isRevised}
            isSuggest={showOfferSuccess?.isSuggest}
          />

          {otherAccount && (
            <CreateNewOffer
              isModalOpen={showCreateOfferModal}
              avatarSrc={renderAvatar(otherProfile.avatar.data.urls, true)}
              username={otherAccount.username}
              hashId={otherAccount.hash_id}
              successOpen={handleOfferSuccess}
              createNewOffer={handleCreateNewOffer}
              closeModal={() => {
                dispatch(showCreateNewOfferModal(false));
                setShowCreateOfferModal(false);
              }}
              currencyInitial={otherAccount?.currency?.data?.initial}
              currencyLabel={otherAccount?.currency?.data?.label_unicode}
              currentUserIsGenerous={isGenerous}
              sex={otherProfile.sex}
              sendSuggestion={handleSendSuggestion}
              cannot_make_offer_reasons={myProfile.cannot_make_offer_reasons}
              buttonID="interestedOffers"
              isSuggestRequest={false}
              title={isGenerous ? 'Make an Offer!' : 'Request A Date!'}
              pageTitle={'Make Request'}
              defaultOfferAmount={otherAccount.profile.data.default_offer_amount}
            />
          )}
        </Content>
      </Form>
    </Fragment>
  );
};

export default OfferPanelV2;
